<template>
    <div>
        <p v-if="error" class="text-danger">{{ errorMsg }}</p>
        <div>
            <b-form-row>
                <b-col cols="8">
                    <p class="text-secondary text-uppercase font-weight-bold">Edit Item:</p>
                    <p class="font-size-100 text-secondary">
                        <b-icon :icon="typeIconName" class="mr-2" font-scale="1.2"></b-icon>
                        {{ typeFriendlyName }}
                    </p>
                </b-col>
                <b-col cols="4" class="text-right">
                    <b-form-group
                        id="fieldset-required"
                        label="Required?"
                        label-for="switch-required"
                    >
                        <b-form-checkbox :checked="localValue.required" @input.native="updateValue('required', $event.target.checked)" id="switch-required" switch style="cursor: pointer;"></b-form-checkbox>
                    </b-form-group>
                </b-col>
            </b-form-row>

            <b-form-group
                id="fieldset-label"
                label="Question"
                label-for="input-label"
                invalid-feedback="Required"
                :state="labelValid"
            >
                <b-form-textarea autofocus id="input-label" trim rows="2" required :state="labelValid" :value="localValue.label" @input.native="updateValue('label', $event.target.value)"></b-form-textarea>
            </b-form-group>

            <b-form-group
                v-if="localValue.type === 'django-money-field'"
                id="fieldset-money"
                label="Currency"
                description="Select the relevant currency."
                label-for="select-currency"
            >
                <b-form-select id="select-currency" :options="currencyChoices" :value="localValue.currency ? localValue.currency : ''" @input.native="updateValue('currency', $event.target.value)"></b-form-select>    
            </b-form-group>

            <b-form-group
                id="fieldset-help"
                label="Explain the Question"
                label-for="input-help"
            >
                <template v-slot:label>
                    Explain the Question
                    <span class="small text-muted ml-2">Optional</span>
                </template>
                <b-form-textarea id="input-help" trim rows="2" :value="localValue.help_text" @input.native="updateValue('help_text', $event.target.value)"></b-form-textarea>
            </b-form-group>
            
            <b-form-group
                v-if="hasChoices && localValue.type !== 'simple-rating'"
                id="fieldset-choices"
                label="Choices"
                description="Enter each choice separated by a comma"
                label-for="input-choices"
                invalid-feedback="You must enter at least two choices for this field type."
                :state="choicesValid"
            >
                <b-form-textarea id="input-choices" :state="choicesValid" required trim rows="2" :value="localValue.choices" @input.native="updateValue('choices', $event.target.value)"></b-form-textarea>
            </b-form-group>

            <b-form-group
                v-if="localValue.type === 'simple-rating'"
                id="fieldset-simple-rating-choices"
                label="Rating Scale"
                description="Select the rating scale."
                label-for="select-simple-rating-choices"
                invalid-feedback="You must select a rating scale."
                :state="choicesValid"
            >
                <b-form-select id="select-simple-rating-choices" :options="simpleRatingChoicesOptions" :value="localValue.choices" @input.native="updateValue('choices', $event.target.value)"></b-form-select>
            </b-form-group>
            
            <div class="d-flex justify-content-between">
                <div class="w-50">
                    <b-button @click="doneEdit" block variant="secondary">Done Editing</b-button>
                </div>
                <div class="">
                    <b-button @click="removeMeByUuid" variant="outline-danger" block>
                        <b-icon icon="trash" aria-hidden="true"></b-icon> Delete
                        <span class="sr-only">Delete</span>
                    </b-button>
                </div>
            </div>

        </div>
    </div>
</template>

<script>
import { emptyItem, itemTypes, simpleRatingChoicesOptions } from '../../js/formium-helpers';
import { tenantInfoMixin } from '../../js/custom-vue-mixins';
import cloneDeep from 'lodash/cloneDeep';

export default {
    name: 'FormiumFieldEditor',
    components: {},
    mixins: [
        tenantInfoMixin,
    ],
    props: {
        value: {
            type: Object,
        },
    },
    data: function() {
        return {
            errorMsg: 'Error',
            error: false,
            fieldTypeOptions: itemTypes,
            collapseIsOpen: false,
            simpleRatingChoicesOptions: simpleRatingChoicesOptions,
        }
    },
    computed: {
        hasValue: function() {
            return this.value ? true : false;
        },
        itemType: function() {
            return this.value ? this.value.itemType : {};
        },
        hasChoices: function() {
            return this.itemType.hasChoices;
        },
        choicesValid: function() {
            return this.value ? this.value.choicesValid() : false;
        },
        labelValid: function() {
            return this.value ? this.value.labelValid() : false;
        },
        // DON'T CHECK OVERALL VALIDITY HERE... SINCE IT WOULD ONLY GET CALLED WHEN ITEM IS LOADED FOR EDIT, NOT WHEN ENTIRE PAGE LOADS
        localValue: function() {
            // IMPORTANT:  we do this to make sure we handle the null state (no value passed yet)
            return this.value ? this.value : emptyItem;
        },
        typeFriendlyName: function() {
            return this.value ? this.itemType.text : '';
        },
        typeIconName: function() {
            return this.value ? this.itemType.iconName : '';
        },
        currentIndex: function() {
            // 'position' in object passed in is base 1, we need base 0 -- neede for knowing which item to DELETE
            if (this.hasValue) return this.localValue.position - 1;
            return null;
        },
        currencyDefault: function() {
            return this.tenantInfo.settings.currency_settings.currencyDefault;
        },
        currencyChoices: function() {
            // need to add "allow any" first option on the list, which you'll combine with data fetch for org defaults
            var allowAllOption = {value: '', text: 'Any - User Selectable'};
            let currencyArray = cloneDeep(this.tenantInfo.settings.currency_settings.currencyChoices);
            currencyArray.unshift(allowAllOption);   // this mutates array, does not make new one
            return currencyArray;
        },
    },
    methods: {
        handleError: function(error, message) {
            console.log(message);
            console.log(error);
            this.errorMsg = message;
            this.error = true;
        },
        updateValue: function(key, newValue) {
            var newValueObject = { ...this.localValue, [key]: newValue };
            this.$emit('input', newValueObject);
        },
        doneEdit: function() {
            // console.log("hello from doneEdit");
            this.$emit('done-edit');
        },
        /* removeMe: function() {
            // console.log("Remove me!  from index #: " + this.currentIndex);
            // console.log("Here's the raw value object requesting to remove: " + JSON.stringify(this.value));
            this.$emit('remove-item', this.currentIndex);
        }, */
        removeMeByUuid: function() {
            // new version of removeMe, pass up uuid of the FormiumFormBuilderItem instead of index which you were doing before
            this.$emit('remove-item-by-uuid', this.value.uuid)
        },
    }
}
</script>

<style>

</style>
